import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { Dropdown, DropdownItem, DropdownList, Input } from '@cokitchen/cokitchen-components';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg';
import { PartnerType } from 'models/partner.model';
import { apiService } from 'utils/api';
import useDashboardContext from 'contexts/dashboard.context/context';

type Props = {
  partnerId: string;
  setPartnerId: (id: string) => void;
};

const PartnerSelector: FC<Props> = ({ partnerId, setPartnerId }) => {
  const { activeCokitchen } = useDashboardContext();
  const [search, setSearch] = useState('');
  const [partners, setPartners] = useState<PartnerType[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<PartnerType | null>(null);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadPartners = useCallback(
    async (searchTerm?: string) => {
      if (!activeCokitchen?.id) return;

      setLoading(true);
      try {
        const res = await apiService.post('logistics/delivery-partner', {
          baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY,
          data: {
            cokitchen_id: [activeCokitchen.id],
            search: searchTerm || undefined
          }
        });

        setPartners(res?.data?.delivery_partners?.results ?? []);
      } finally {
        setLoading(false);
      }
    },
    [activeCokitchen?.id]
  );

  const debouncedFetch = useMemo(() => debounce(loadPartners, 400), [loadPartners]);
  useEffect(() => {
    return () => debouncedFetch.cancel();
  }, [debouncedFetch]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    debouncedFetch(value);
  };

  const handleSelectedPartner = (partner: PartnerType) => {
    setPartnerId(partner.id);
    setSelectedPartner(partner);
    setOpenDropdown(false);
  };

  const dropdownLabel = useMemo(() => selectedPartner?.name || 'All Partners', [selectedPartner]);

  return (
    <Dropdown
      visible={openDropdown}
      onClickOutside={() => setOpenDropdown(false)}
      content={
        <button
          type='button'
          onClick={() => setOpenDropdown((v) => !v)}
          className='px-3 py-[6px] inline-flex items-center gap-1 text-sm font-medium rounded-[20px] bg-red-1'>
          {dropdownLabel}
          <ChevronDownIcon />
        </button>
      }>
      <DropdownList className='min-w-[300px]'>
        <div className='px-5 border-b border-dashed border-b-[#ebebeb]'>
          <Input
            type='search'
            inputClass='py-2 text-sm'
            placeholder='Search partner'
            small
            value={search}
            onChange={handleSearchChange}
          />
        </div>

        <ul className='max-h-[300px] overflow-auto'>
          <DropdownItem>
            <Input
              type='radio'
              className='text-sm'
              checked={!partnerId}
              onChange={() => setSelectedPartner({} as PartnerType)}
              label='All Partners'
            />
          </DropdownItem>

          {loading ? (
            <DropdownItem isLabel>Loading…</DropdownItem>
          ) : (
            partners.map((p) => (
              <DropdownItem key={p.id}>
                <Input
                  className='text-sm'
                  type='radio'
                  checked={partnerId === p.id}
                  onChange={() => handleSelectedPartner(p)}
                  label={p.name}
                />
              </DropdownItem>
            ))
          )}
        </ul>
      </DropdownList>
    </Dropdown>
  );
};

export default PartnerSelector;
